import React, { forwardRef, type Ref, useEffect, useRef, useState } from "react";
import {
  CountrySelectorDropdown,
  defaultCountries,
  FlagImage,
  parseCountry,
  type ParsedCountry
} from "react-international-phone";
import {type Country, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber, type Value } from "react-phone-number-input";
import Input from "react-phone-number-input/input";
import DOMPurify from "dompurify";
import { useDebouncedCallback } from "use-debounce";

// import ChevronDown from "@/components/ui/PhoneInput/icons/chevron-down.svg?react";
import StyledCustomPhoneInput from "@/components/ui/PhoneInput/StyledCustomPhoneInput";

import { cn } from "@/utils/helpers/classnames";

import "react-international-phone/style.css";
import { ChevronDown } from "lucide-react";

export interface CustomPhoneInputProps {
  countryCodeLabel?: string;
  countryCodeLabelIcon?: React.ReactNode;
  phoneInputLabel?: string;
  phoneInputLabelIcon?: string;
  label?: string;
  labelIcon?: React.ReactNode;
  required?: boolean;
  type?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  icon?: boolean;
  $marginbottom?: string;
  $border?: string;
  $borderRadius?: string;
  height?: string;
  value?: string | number | null;
  onBlur?: (value: string) => void;
  readOnly?: boolean;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  $bgColor?: string;
  $dataId?: string;
  showFlag?: boolean;
  supportedCountries?: { code: string; name: string }[];
  $maxLength?: number;
  defaultCountry?: string;
}

const CustomPhoneInput = forwardRef(
  (
    {
      countryCodeLabel,
      countryCodeLabelIcon,
      phoneInputLabel,
      phoneInputLabelIcon,
      label,
      labelIcon,
      required,
      onChange,
      onKeyDown,
      error,
      $marginbottom,
      $border,
      $borderRadius,
      height,
      onBlur,
      value = "",
      prefixIcon,
      suffixIcon,
      disabled,
      placeholder,
      $dataId,
      $bgColor,
      supportedCountries = [],
      showFlag = true,
      defaultCountry = "GB",
      $maxLength,
      ...props
    }: CustomPhoneInputProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasError, setHasError] = useState(error);
    const [countryCode, setCountryCode] = useState(defaultCountry.toUpperCase());
    const [dialCode, setDialCode] = useState<string>(
      parsePhoneNumber(String(value))?.countryCallingCode ?? getCountryCallingCode(defaultCountry as Country)
    );
    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    useEffect(() => {
      if (error) {
        setHasError(error);
      }
    }, [error]);

    useEffect(() => {
      try {
        const phoneNumber = parsePhoneNumber(String(value));

        if (phoneNumber?.isValid()) {
          const country = phoneNumber.country ?? "";
          const dialCode = phoneNumber.countryCallingCode;

          setCountryCode(country.toUpperCase());
          setDialCode(dialCode);
        }
      } catch (e) {
        console.error("Invalid phone number:", e);
      }
    }, [value]);

    const countries = defaultCountries.filter((country) => {
      const { iso2 } = parseCountry(country);
      const allowedCountries =
        Array.isArray(supportedCountries) &&
        supportedCountries.length > 0 &&
        supportedCountries.map((item) => item.code.toLowerCase());
      return Array.isArray(allowedCountries) && allowedCountries.length > 0
        ? allowedCountries.includes(iso2)
        : defaultCountries;
    });

    const handleSetCountry = (data: ParsedCountry) => {
      setCountryCode(data.iso2.toUpperCase());
      setDialCode(data.dialCode);
      setShowDropdown(false);
    };

    const checkIsValid = (data: string) => {
      if (isValidPhoneNumber(data)) {
        setHasError("");
      } else {
        setHasError("Invalid Number");
      }
    };

    const handleChange = useDebouncedCallback((data: string | Node | undefined) => {
      if (!data) {
        return;
      }
      const sanitizedValue = DOMPurify.sanitize(data);

      if ($maxLength && sanitizedValue.length > $maxLength) return; // ✅ enforce maxLength
      if (onChange) {
        onChange(sanitizedValue);
        checkIsValid(sanitizedValue);
      }
    }, 300);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const sanitizedValue = DOMPurify.sanitize(e.target.value);
      if (onBlur) {
        onBlur(sanitizedValue);
      }
    };

    return (
      <StyledCustomPhoneInput
        ref={ref}
        $marginbottom={$marginbottom ?? "2rem"}
        $border={hasError ? "#eb5757" : ($border ?? "#D1D5DB")}
        $borderRadius={$borderRadius ?? "0.5rem"}
        height={height ?? "2.75rem"}
        required={required}
        $bgColor={$bgColor}
        $dataId={$dataId}
        {...props}
        data-disabled={disabled}
      >
        <div className='mainWrapper'>
          {(label ?? labelIcon) ? (
            <label>
              {labelIcon ? labelIcon : null}
              {label ? label : null}
            </label>
          ) : null}
          <div className='subWrapperGroup'>
            <div className='subWrapper'>
              {(countryCodeLabelIcon ?? countryCodeLabel) ? (
                <label>
                  {countryCodeLabelIcon ? countryCodeLabelIcon : null}
                  {countryCodeLabel ? countryCodeLabel : null}
                </label>
              ) : null}

              <div className='selectWrapper' data-disabled={disabled}>
                <div className='select-container'>
                  {showFlag ? (
                    <span
                      onClick={() => {
                        setShowDropdown(true);
                      }}
                    >
                      <FlagImage iso2={countryCode.toLowerCase()} size='30px' />
                    </span>
                  ) : null}
                  <CountrySelectorDropdown
                    show={showDropdown}
                    selectedCountry={countryCode.toLowerCase()}
                    onSelect={(data) => {
                      handleSetCountry(data);
                    }}
                    onClose={() => {
                      setShowDropdown(false);
                    }}
                    style={{ zIndex: 1000, width: "max-content" }}
                    countries={countries}
                    listItemFlagClassName='hidden lg:block'
                    listItemDialCodeClassName='text-sm lg:text-base'
                    listItemClassName='w-full'
                    listItemCountryNameClassName='text-sm lg:text-base'
                  />

                  <span
                    className={cn("'dialCode'", { disabled: disabled })}
                    onClick={() => {
                      setShowDropdown(true);
                    }}
                  >
                    +{dialCode} <ChevronDown />
                  </span>
                </div>
                {suffixIcon ? <div className='suffixIcon'>{suffixIcon ? suffixIcon : null}</div> : null}
              </div>
            </div>
            <div className='subWrapper'>
              {(phoneInputLabel ?? phoneInputLabelIcon) ? (
                <label>
                  {phoneInputLabelIcon ? phoneInputLabelIcon : null}
                  {phoneInputLabel ? phoneInputLabel : null}
                </label>
              ) : null}
              <div className='inputWrapper' data-disabled={disabled}>
                {prefixIcon ? <div className='prefixIcon'>{prefixIcon ? prefixIcon : null}</div> : null}
                <div className='input-container'>
                  <Input
                    value={value as Value}
                    country={countryCode as Country}
                    onChange={(data) => {
                      handleChange(data);
                    }}
                    required={required}
                    onBlur={handleBlur}
                    disabled={disabled}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    {...props}
                    ref={inputRef}
                    data-id={$dataId}
                  />
                </div>
                {suffixIcon ? <div className='suffixIcon'>{suffixIcon ? suffixIcon : null}</div> : null}
              </div>
            </div>
          </div>
        </div>
        {hasError ? <span className='error'>{hasError}</span> : ""}
      </StyledCustomPhoneInput>
    );
  }
);

CustomPhoneInput.displayName = "Input";

export default CustomPhoneInput;
